import ListViewModel from "viewModels/listViewModel";
import { action, computed, observable } from "mobx";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { Router } from "@frui.ts/screens";
import ExternalReportsRepository from "repositories/externalReportsRepository";
import EventBus from "services/eventBus";
import SecurityService from "services/securityService";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import ExternalReportType from "entities/externalReportType";
import ExternalReportResponse from "entities/externalReportResponse";
import { watchBusy } from "utils/busyWatcher";
import { allowToDownload } from "../rights";
import { Modules } from "config";
import * as Sentry from "@sentry/react";
import download from "downloadjs";
import parseFileNameFromAxiosResponse from "utils/parseFileNameFromAxiosResponse";
import DateRangeRequest, { DateRangeValidationRules } from "entities/dateRangeRequest";
import { attachAutomaticValidator, validate } from "@frui.ts/validation";
import type ExternalReportDetailViewModel from "../externalReportDetailViewModel";

const navigationName = "ministry-of-transport";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
})
export default class MinistryOfTransportReportsListViewModel extends ListViewModel<
  ExternalReportResponse,
  ExternalReportDetailViewModel,
  ScreenBase,
  any
> {
  readonly modelName = "externalReportResponse";
  @observable items: ExternalReportResponse[] = [];
  @observable fileResponse: any = undefined;
  @observable request = new DateRangeRequest();

  readonly idProperty = "id";

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get canMakeReport() {
    return this.securityService.allowTo("charges.reports", "write");
  }

  constructor(
    protected detailViewModelFactory: ReturnType<typeof ExternalReportDetailViewModel.Factory>,
    public repository: ExternalReportsRepository,
    public securityService: SecurityService,
    public eventBus: EventBus,
    public localizationService: LocalizationService,
    public notificationService: NotificationService
  ) {
    super();
    this.navigationName = navigationName;

    attachAutomaticValidator(this.request, DateRangeValidationRules);
  }

  protected onActivate(): Promise<void> {
    this.loadData();
    return super.onActivate();
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.timeCharges, `externalReports.ministryOfTransport.${code}`, size);
  }

  @action.bound
  @watchBusy
  async onFormSubmit() {
    if (!validate(this.request)) {
      return;
    }
    await this.onGenerateReportClick(this.request.from ?? new Date(), this.request.to ?? new Date());
  }

  @action.bound
  @watchBusy
  async onGenerateReportClick(fromDate: Date, toDate: Date) {
    try {
      const result = await this.repository.generateNewMinistryOfTransportReport(fromDate, toDate);

      if (result.reportRequestId) {
        const reportName = `md_report_${new Date().toISOString()}.xlsx`;

        await this.download({ id: result.reportRequestId, reportName: reportName, createdBy: "", status: result.reportStatus });
        this.notificationService.add({ title: this.translate("alreadyGeneratedMessage"), type: NotificationType.SUCCESS });
      } else {
        this.notificationService.add({ title: this.translate("generatingMessage"), type: NotificationType.SUCCESS });
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  @action.bound
  @watchBusy
  async onDownload(item: ExternalReportResponse) {
    await this.download(item);
  }

  @computed
  get allowToDownload() {
    if (!allowToDownload(this.securityService)) {
      this.notificationService.addNotAllowedError();
      return false;
    }
    return true;
  }

  protected async fetchData() {
    const reports = await this.repository.getExternalReports(ExternalReportType.Mot);

    reports.items = reports.items.sort((a, b) => {
      if (!a.processedAt || !b.processedAt) {
        return -1;
      }
      return b.processedAt.getTime() - a.processedAt.getTime();
    });

    return reports;
  }

  async download(item: ExternalReportResponse) {
    try {
      if (this.allowToDownload) {
        this.fileResponse = await this.repository.downloadReport(item.id);
        download(this.fileResponse.data, parseFileNameFromAxiosResponse(this.fileResponse, item.reportName), "application/zip");
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }
}
